import { ChevronLeft, ChevronRight } from "@carbon/icons-react";
import { useDateFormatter } from "@react-aria/i18n";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { type CalendarState, type RangeCalendarState } from "@react-stately/calendar";
import { type DOMAttributes } from "@react-types/shared";
import cx from "classnames";
import { type FC, useRef } from "react";
import { type AriaButtonProps, mergeProps, useButton, useFocusRing } from "react-aria";

export const CalendarHeader: FC<{
	state: CalendarState | RangeCalendarState;
	calendarProps: DOMAttributes;
	prevButtonProps: AriaButtonProps;
	nextButtonProps: AriaButtonProps;
	visibleDurationMonths?: 1 | 2;
}> = ({ state, calendarProps, prevButtonProps, nextButtonProps, visibleDurationMonths = 1 }) => {
	const monthDateFormatter = useDateFormatter({
		month: "long",
		year: "numeric",
		timeZone: state.timeZone,
	});

	return (
		<div className="flex items-center pb-2">
			{/* Add a screen reader only description of the entire visible range rather than
			 * a separate heading above each month grid. This is placed first in the DOM order
			 * so that it is the first thing a touch screen reader user encounters.
			 * In addition, VoiceOver on iOS does not announce the aria-label of the grid
			 * elements, so the aria-label of the Calendar is included here as well. */}
			<VisuallyHidden>
				<h2>{calendarProps["aria-label"]}</h2>
			</VisuallyHidden>
			<CalendarButton {...prevButtonProps} size="sm" variant="icon">
				<ChevronLeft size={12} />
			</CalendarButton>
			<h2
				// We have a visually hidden heading describing the entire visible range,
				// and the calendar itself describes the individual month
				// so we don't need to repeat that here for screen reader users.
				aria-hidden
				className="flex-1 items-center text-center text-xl font-bold"
			>
				{monthDateFormatter.format(state.visibleRange.start.toDate(state.timeZone))}
			</h2>
			{visibleDurationMonths > 1 && (
				<h2 aria-hidden className="flex-1 items-center text-center text-xl font-bold">
					{monthDateFormatter.format(
						state.visibleRange.start.add({ months: 1 }).toDate(state.timeZone)
					)}
				</h2>
			)}
			<CalendarButton {...nextButtonProps} size="sm" variant="icon">
				<ChevronRight size={12} />
			</CalendarButton>
		</div>
	);
};

export function CalendarButton(props) {
	const ref = useRef<HTMLButtonElement>(null);
	const { buttonProps } = useButton(props, ref);
	const { focusProps, isFocusVisible } = useFocusRing();
	return (
		<button
			{...mergeProps(buttonProps, focusProps)}
			ref={ref}
			className={cx("p-2", "rounded-full", "outline-none", "ring-default", {
				"bg-button-normal-secondary-disabled text-secondary-disabled": props.isDisabled,
				"bg-button-normal-secondary-default hover:bg-button-normal-secondary-hover focus:bg-button-normal-secondary-active focus:shadow-border-brand-focus focus:ring-4 active:bg-button-normal-secondary-active active:shadow-border-brand-active active:ring-4":
					!props.isDisabled,
				"ring-4": isFocusVisible,
			})}
		>
			{props.children}
		</button>
	);
}
